import {
    Box,
    Typography,
} from '@mui/material';
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../../components/MRTDataGrid/TableActionButtons';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoggedInUser } from '../../../app/hooks/useLoggedInUser';
import { useNotificationStack } from '../../../app/hooks/useNotificationStack';
import { useConfirmDialogContext } from '../../../components/ConfirmDialog';
import {
    useDeleteCustomerBrandMutation,
    useGetCustomerBrandsQuery
} from '../../../app/redux-fetch/apiProducts';
import AddButton from '../../../components/Buttons/AddButton';
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender';
import MRTDataGridV2 from '../../../components/MRTDataGrid/MRTDataGridv2';
import YesNoTypography from '../../../components/YesNoTypograpphy/YesNoTypography';
import SideModalWrapper from '../../Assets/modals/SideModalWrapper';
import { messages } from '../messages';
import { Brand } from '../types';
import CustomerBrandForm from './CustomerBrandForm';

const CustomerBrandTable: React.FC = () => {
    const { formatMessage } = useIntl();
    const { customerId } = useLoggedInUser();

    const { data = [], isLoading, refetch } = useGetCustomerBrandsQuery({ customerId: customerId ?? -1 });
    const [deleteCustomerBrand] = useDeleteCustomerBrandMutation();
    const { enqueueSuccess, enqueueFailure } = useNotificationStack();
    const { confirm } = useConfirmDialogContext();

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [viewOpen, setViewOpen] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState(-1);


    const columns = useMemo<MRT_ColumnDef<Brand>[]>(
        () => [
            {
                accessorKey: 'name',
                Cell: ({ renderedCellValue }: any) => {
                    return <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>
                },
                header: formatMessage(messages.name),
            },
            {
                accessorKey: 'systemDefined',
                header: formatMessage(messages.systemDefined),
                muiTableHeadCellProps: {
                    align: 'center',
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
            {
                accessorKey: 'inUse',
                header: formatMessage(messages.inUse),
                muiTableHeadCellProps: {
                    align: 'center',
                },
                muiTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
        ],
        [formatMessage]
    );

    return (
        <>
            <SideModalWrapper
                open={createOpen}
                key={'create'}
                headerLabel={formatMessage(messages.createEditMake)}
                onClose={() => {
                    setCreateOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <CustomerBrandForm />
            </SideModalWrapper>
            <SideModalWrapper
                open={editOpen}
                key={'edit'}
                headerLabel={formatMessage(messages.createEditMake)}
                onClose={() => {
                    setEditOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <CustomerBrandForm brandId={selectedBrandId} />
            </SideModalWrapper>
            <SideModalWrapper
                open={viewOpen}
                key={'view'}
                headerLabel={formatMessage(messages.viewBrandModal)}
                onClose={() => {
                    setViewOpen((pv) => !pv);
                }}
                withBottomBox={false}
            >
                <CustomerBrandForm brandId={selectedBrandId} isDisabled={true} />
            </SideModalWrapper>

            <MRTDataGridV2
                initialState={{
                    pagination: { pageSize: 100, pageIndex: 0 },
                    density: 'comfortable',
                    sorting: [{ id: 'name', desc: false }],
                }}
                state={{
                    isLoading,
                }}
                rightHeadingComponent={
                    <AddButton
                        text={formatMessage(messages.add)}
                        onClick={() => setCreateOpen(true)}
                    />
                }
                columns={columns}
                data={data}
                getRowId={(row) => row.id as any}
                enableEditing
                enableRowActions
                positionActionsColumn='last'
                renderRowActions={({ row }: any) => {
                    const brandId = row.original.id;

                    return (
                        <Box sx={{ display: 'flex' }}>
                            <ViewActionButton
                                onClick={() => {
                                    setSelectedBrandId(brandId);
                                    setViewOpen(true);
                                }}
                            />
                            <EditActionButton
                                onClick={() => {
                                    setSelectedBrandId(brandId);
                                    setEditOpen(true);
                                }}
                            />
                            <ConditionalRender condition={!row.original.inUse}>
                                <DeleteActionButton
                                    onClick={async () => {
                                        const confirmed = await confirm({
                                            title: formatMessage(messages.deleteBrandTitle),
                                            message: formatMessage(messages.deleteBrandDescription),
                                            confirmText: formatMessage(messages.deleteBtn),
                                            cancelText: formatMessage(messages.cancel),
                                            severity: 'warning',
                                            confirmButtonColor: 'error',
                                            cancelButtonColor: 'secondary',
                                        })

                                        if (confirmed) {
                                            deleteCustomerBrand({ customerId: customerId ?? -1, brandId: row.original.id })
                                                .unwrap()
                                                .then(() => {
                                                    enqueueSuccess(messages.brandDeleted)
                                                })
                                                .catch(() => {
                                                    enqueueFailure(messages.brandDeleteError)
                                                })
                                        }
                                    }}
                                />
                            </ConditionalRender>
                        </Box>
                    );
                }}
            />
        </>
    );
};

export default CustomerBrandTable;
